import { formatDateTime } from "@/lib/hooks/utils";
import { PersonListResponseDto } from "@/lib/interfaces/persons";
import { DataTableProps } from "@/lib/interfaces/utils";
import { Button } from "@/ui-components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui-components/ui/table";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Building2, Mail, Phone, User } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

const PersonDataTable = ({
  data,
  isLoading,
  pageCount,
  pagination,
  setPagination,
}: DataTableProps<PersonListResponseDto>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const columns: ColumnDef<PersonListResponseDto>[] = [
    {
      accessorKey: "name",
      header: () => (
        <div className="flex items-center">
          <p>Name</p>
        </div>
      ),
      cell: ({ row }) => {
        const fullName = `${row.original.firstName} ${row.original.lastName || ""}`.trim();
        return (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span className="font-medium">{fullName}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "contact",
      header: () => (
        <div className="flex items-center">
          <p>Mail</p>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          {row.original.mail ? (
            <Link
              className="flex items-center gap-2 cursor-pointer"
              to={`mailto:${row.original.mail}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
            >
              <Button variant="link" className="flex items-center gap-2 h-fit p-0">
                <Mail className="h-4 w-4" />
                <span>{row.original.mail}</span>
              </Button>
            </Link>
          ) : (
            <span>-</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: "phone",
      header: () => (
        <div className="flex items-center">
          <p>Telefon</p>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          {row.original.phone ? (
            <Link
              className="flex items-center gap-2 cursor-pointer"
              to={`tel:${row.original.phone}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
            >
              <Button variant="link" className="flex items-center gap-2 h-fit p-0">
                <Phone className="h-4 w-4" />
                <span>{row.original.phone}</span>
              </Button>
            </Link>
          ) : (
            <span>-</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: "organization",
      header: () => (
        <div className="flex items-center">
          <p>Organisation</p>
        </div>
      ),
      cell: ({ row }) =>
        row.original.organizationName && (
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            <span>{row.original.organizationName}</span>
          </div>
        ),
    },
    {
      accessorKey: "updatedAt",
      header: () => (
        <div className="flex items-center">
          <p>Aktualisiert am</p>
        </div>
      ),
      cell: ({ row }) => <div>{row.original.updatedAt ? formatDateTime(row.original.updatedAt) : ""}</div>,
    },
    {
      accessorKey: "createdAt",
      header: () => (
        <div className="flex items-center">
          <p>Erstellt am</p>
        </div>
      ),
      cell: ({ row }) => <div>{formatDateTime(row.original.createdAt)}</div>,
    },
  ];

  const table = useReactTable({
    data,
    columns,
    pageCount,
    manualPagination: true,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: { pagination: { pageSize: 20 } },
    state: {
      pagination,
    },
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer hover:bg-gray-100"
                  onClick={() => navigate(`/contacts/persons/${row.original._id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Keine Personen gefunden
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between space-x-2 py-4">
        <Button
          className="text-[17px] text-[#888] hover:text-[#12282A]"
          variant="outline"
          size="sm"
          onClick={() => {
            table.previousPage();
            const params = new URLSearchParams(searchParams);
            params.set("page", table.getState().pagination.pageIndex.toString());
            setSearchParams(params);
          }}
          disabled={!table.getCanPreviousPage()}
        >
          Letzte
        </Button>
        <span className="text-sm text-[#D0D5DD]">
          Seite {table.getState().pagination.pageIndex + 1} von {table.getPageCount()}
        </span>
        <Button
          className="text-[17px] text-[#888] hover:text-[#12282A]"
          variant="outline"
          size="sm"
          onClick={() => {
            table.nextPage();
            const params = new URLSearchParams(searchParams);
            params.set("page", (table.getState().pagination.pageIndex + 2).toString());
            setSearchParams(params);
          }}
          disabled={!table.getCanNextPage()}
        >
          Nächste
        </Button>
      </div>
    </div>
  );
};

export default PersonDataTable;
